import { Link } from "../lib/route";
import { ArrowRight, PhoneCall } from "lucide-react";
import { SITE, COPY } from "../lib/_data";

/**
 * Big full-width CTA banner with background image + gradient overlay.
 * Used at the bottom of every page for consistency.
 */
export default function CTABanner({
  image,
  eyebrow = "Ready when you are",
  title = "Let's get started on your project.",
  subtitle = "Book a free consultation, request a quote, or just call us to talk it through. No pressure — ever.",
  primary = { label: "Book a Consultation", to: "/consultations" },
  secondary = { label: "Request a Quote", to: "/contact" },
  showPhone = true,
}) {
  return (
    <section
      data-testid="cta-banner"
      className="relative w-full min-h-[60vh] md:min-h-[70vh] overflow-hidden grain"
    >
      {image ? (
        <img
          src={image}
          alt=""
          data-testid="cta-image"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div
          className="absolute inset-0 w-full h-full"
          style={{ background: "linear-gradient(120deg, #2c2a29 0%, #c85a3c 100%)" }}
          aria-hidden="true"
          data-testid="cta-image"
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(44,42,41,0.85) 0%, rgba(200,90,60,0.65) 100%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
        <div className="max-w-2xl">
          <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#f5efe4]" data-testid="cta-eyebrow">
            {eyebrow}
          </span>
          <h2
            data-testid="cta-title"
            className="font-display text-4xl md:text-6xl font-black text-[#fffdf9] leading-[1.05] mt-4"
          >
            {title}
          </h2>
          <p className="mt-5 text-[#f5efe4] max-w-xl leading-relaxed" data-testid="cta-subtitle">
            {subtitle}
          </p>
        </div>

        <div className="flex flex-col gap-3 md:min-w-[280px]">
          <Link
            to={primary.to}
            data-testid="cta-primary"
            className="inline-flex items-center justify-center gap-2 bg-[#fffdf9] text-[#2c2a29] hover:bg-[#c85a3c] hover:text-[#fffdf9] font-bold px-7 py-4 rounded-full text-base transition-all hover:-translate-y-0.5"
          >
            {primary.label} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={secondary.to}
            data-testid="cta-secondary"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#fffdf9] text-[#fffdf9] hover:bg-[#fffdf9] hover:text-[#2c2a29] font-bold px-7 py-4 rounded-full text-base transition-colors"
          >
            {secondary.label}
          </Link>
          {showPhone && SITE.phone && (
            <a
              href={SITE.phoneHref}
              data-testid="cta-call"
              className="inline-flex items-center justify-center gap-2 text-[#fffdf9] font-bold text-sm mt-1 hover:text-[#f5efe4]"
            >
              <PhoneCall className="w-4 h-4" /> {(COPY.shared && COPY.shared.ctaCallPrefix) || "or call"} {SITE.phone}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
